import React from "react";

export function cn(...a) {
  return a.filter(Boolean).join(" ");
}

export function Button({ variant = "primary", size = "md", className, disabled, loading, children, ...props }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg font-bold transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/70";
  const variants = {
    primary: "bg-primary text-primary-foreground hover:brightness-110 shadow-lg shadow-primary/20",
    accent: "bg-accent text-accent-foreground hover:brightness-110 shadow-lg shadow-accent/20",
    outline: "border border-border bg-card/60 text-foreground hover:border-primary/60 hover:bg-card",
    ghost: "text-foreground/90 hover:bg-white/5",
    danger: "bg-destructive text-white hover:brightness-110",
    subtle: "bg-white/5 text-foreground hover:bg-white/10",
  };
  const sizes = { sm: "h-9 px-3 text-xs", md: "h-11 px-5 text-sm", lg: "h-12 px-6 text-base", icon: "h-10 w-10" };
  return (
    <button className={cn(base, variants[variant], sizes[size], className)} disabled={disabled || loading} {...props}>
      {loading && <Spinner size={16} />}
      {children}
    </button>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div className={cn("abl-card p-5", className)} {...props}>
      {children}
    </div>
  );
}

export function Input({ className, label, error, ...props }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span>}
      <input
        className={cn(
          "w-full rounded-lg border border-input bg-black/20 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50",
          "focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/50",
          error && "border-destructive",
          className
        )}
        {...props}
      />
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
}

export function Textarea({ className, label, rows = 4, ...props }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span>}
      <textarea
        rows={rows}
        className={cn(
          "w-full rounded-lg border border-input bg-black/20 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/50",
          className
        )}
        {...props}
      />
    </label>
  );
}

export function Select({ className, label, children, ...props }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span>}
      <select
        className={cn(
          "w-full rounded-lg border border-input bg-black/20 px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none",
          className
        )}
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function Badge({ tone = "muted", className, children }) {
  const tones = {
    muted: "bg-white/8 text-muted-foreground",
    primary: "bg-primary/15 text-primary",
    accent: "bg-accent/15 text-accent",
    success: "bg-emerald-500/15 text-emerald-400",
    warning: "bg-amber-500/15 text-amber-400",
    danger: "bg-destructive/15 text-red-400",
  };
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold", tones[tone], className)}>
      {children}
    </span>
  );
}

export function Spinner({ size = 20, className }) {
  return (
    <span
      className={cn("inline-block animate-spin rounded-full border-2 border-current border-t-transparent", className)}
      style={{ width: size, height: size }}
    />
  );
}

export function Empty({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/40 p-12 text-center">
      {Icon && <Icon className="text-muted-foreground/60" size={40} strokeWidth={1.5} />}
      <p className="text-base font-bold text-foreground">{title}</p>
      {hint && <p className="max-w-sm text-sm text-muted-foreground">{hint}</p>}
      {action}
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className={cn("abl-scroll max-h-[90vh] w-full overflow-y-auto rounded-2xl border border-border bg-popover p-6", wide ? "max-w-3xl" : "max-w-lg")}
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h3 className="mb-4 text-lg font-bold">{title}</h3>}
        {children}
      </div>
    </div>
  );
}
